"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { catalogRefreshEvent } from "@/app/_lib/catalog-cache-policy";
import { ProductListCard } from "@/components/catalog/product-list-card";
import { MotionList, MotionListItem } from "@/components/ui/motion-primitives";
import type { PublicCatalogProduct } from "@/domain/catalog/catalog-product";
import type {
  AvailabilityFilter,
  CatalogSearchResult,
  CatalogSort,
} from "@/domain/catalog/search";

export interface InfiniteProductGridProps {
  readonly availability?: AvailabilityFilter;
  readonly familySlug?: string;
  readonly initialProducts: readonly PublicCatalogProduct[];
  readonly pageSize: number;
  readonly productType?: "original" | "generico";
  readonly query?: string;
  readonly sort: CatalogSort;
  readonly total: number;
}

export function InfiniteProductGrid({
  availability,
  familySlug,
  initialProducts,
  pageSize,
  productType,
  query,
  sort,
  total,
}: Readonly<InfiniteProductGridProps>) {
  const resetKey = buildResetKey({
    availability,
    familySlug,
    initialProducts,
    pageSize,
    productType,
    query,
    sort,
    total,
  });

  return (
    <InfiniteProductGridState
      availability={availability}
      familySlug={familySlug}
      initialProducts={initialProducts}
      key={resetKey}
      pageSize={pageSize}
      productType={productType}
      query={query}
      sort={sort}
      total={total}
    />
  );
}

function InfiniteProductGridState({
  availability,
  familySlug,
  initialProducts,
  pageSize,
  productType,
  query,
  sort,
  total,
}: Readonly<InfiniteProductGridProps>) {
  const [products, setProducts] = useState<PublicCatalogProduct[]>([...initialProducts]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [effectiveTotal, setEffectiveTotal] = useState(total);
  const [hasMore, setHasMore] = useState(initialProducts.length < total);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(hasMore);
  const controllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUnmountedRef = useRef(false);
  const productsRef = useRef(products);
  const refreshControllerRef = useRef<AbortController | null>(null);
  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  useEffect(() => {
    // Periodic and visibility refreshes revalidate the first page only. Deeper loaded
    // pages keep their content instead of fanning out one request per loaded offset;
    // they move forward on the next explicit user action (navigation or load more).
    const revalidateFirstPage = () => {
      refreshControllerRef.current?.abort();
      const controller = new AbortController();
      refreshControllerRef.current = controller;
      void fetch(buildSearchUrl({
        availability,
        familySlug,
        limit: pageSize,
        offset: 0,
        productType,
        query,
        sort,
      }), { cache: "no-store", signal: controller.signal })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error("CATALOG_REFRESH_FAILED");
          }
          return (await response.json()) as CatalogSearchResult<PublicCatalogProduct>;
        })
        .then((page) => {
          if (controller.signal.aborted) {
            return;
          }
          const refreshedFirstPage = page.products ?? [];
          const refreshedIds = new Set(refreshedFirstPage.map((product) => product.id));
          const keptDeeperPages = productsRef.current
            .slice(pageSize)
            .filter((product) => !refreshedIds.has(product.id));
          const nextProducts = [...refreshedFirstPage, ...keptDeeperPages];
          setProducts(nextProducts);
          if (typeof page.total === "number") {
            setEffectiveTotal(page.total);
            setHasMore(nextProducts.length < page.total);
          }
        })
        .catch(() => {
          // Keep the previous cards on a transient failure; the next tick retries.
        })
        .finally(() => {
          if (refreshControllerRef.current === controller) {
            refreshControllerRef.current = null;
          }
        });
    };
    window.addEventListener(catalogRefreshEvent, revalidateFirstPage);
    return () => {
      window.removeEventListener(catalogRefreshEvent, revalidateFirstPage);
      refreshControllerRef.current?.abort();
    };
  }, [availability, familySlug, pageSize, productType, query, sort]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const loadNextProducts = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) {
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    setErrorMessage(null);

    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    const controller = new AbortController();
    controllerRef.current = controller;

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 15000);
    timeoutRef.current = timeoutId;

    const currentOffset = products.length;

    try {
      const response = await fetch(
        buildSearchUrl({
          availability,
          familySlug,
          limit: pageSize,
          offset: currentOffset,
          productType,
          query,
          sort,
        }),
        { signal: controller.signal },
      );

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (!response.ok) {
        throw new Error("CATALOG_SEARCH_REQUEST_FAILED");
      }

      const result = (await response.json()) as CatalogSearchResult<PublicCatalogProduct>;
      const nextEffectiveTotal =
        typeof result.total === "number" ? result.total : effectiveTotal;
      setEffectiveTotal(nextEffectiveTotal);

      const raw = result.products ?? [];

      if (raw.length === 0) {
        setHasMore(false);
        hasMoreRef.current = false;
        return;
      }

      const currentIds = new Set(products.map((product) => product.id));
      const unique = raw.filter((product) => !currentIds.has(product.id));

      if (unique.length === 0) {
        setHasMore(false);
        hasMoreRef.current = false;
        return;
      }

      const nextProducts = [...products, ...unique];
      setProducts(nextProducts);

      if (nextProducts.length >= nextEffectiveTotal) {
        setHasMore(false);
        hasMoreRef.current = false;
      } else {
        // Keep hasMore true; already true but ensure ref
        hasMoreRef.current = true;
        setHasMore(true);
      }
    } catch (error) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // If aborted due to unmount, do not show error
      if (isUnmountedRef.current) {
        return;
      }

      const isAbort =
        controller.signal.aborted ||
        (error instanceof DOMException && error.name === "AbortError") ||
        (error instanceof Error && error.name === "AbortError");

      if (isAbort && isUnmountedRef.current) {
        return;
      }

      // For timeout and other aborts while mounted, show recoverable error
      // Also for HTTP failures
      setErrorMessage("No se pudieron cargar más productos.");
      // Keep hasMore true to allow retry
      hasMoreRef.current = true;
      setHasMore(true);
    } finally {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      loadingRef.current = false;
      setIsLoading(false);
      controllerRef.current = null;
    }
  }, [availability, effectiveTotal, familySlug, pageSize, productType, products, query, sort]);

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel || !hasMore || isLoading) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return;
        }

        if (loadingRef.current || !hasMoreRef.current) {
          return;
        }

        void loadNextProducts();
      },
      {
        rootMargin: "720px 0px",
      },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [hasMore, isLoading, loadNextProducts]);

  const isObserverAvailable = typeof IntersectionObserver !== "undefined";

  return (
    <>
      <MotionList className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {products.map((product, index) => (
          <MotionListItem key={product.id} layout="position">
            <ProductListCard
              loading={index < 2 ? "eager" : "lazy"}
              priority={index < 2}
              product={product}
              searchQuery={query}
            />
          </MotionListItem>
        ))}
      </MotionList>
      {hasMore || isLoading || errorMessage ? (
        <div
          aria-live="polite"
          className="mt-6 min-h-10 text-center text-sm font-bold text-muted"
          ref={sentinelRef}
        >
          {errorMessage ? (
            <div className="flex flex-col items-center gap-2">
              <p>{errorMessage}</p>
              <button
                className="rounded-md bg-primary px-4 py-2 text-sm font-bold text-white"
                onClick={() => void loadNextProducts()}
                type="button"
              >
                Reintentar
              </button>
            </div>
          ) : isLoading ? (
            "Cargando productos..."
          ) : hasMore && !isObserverAvailable ? (
            <button
              className="rounded-md border border-primary px-4 py-2 text-sm font-bold text-primary"
              onClick={() => void loadNextProducts()}
              type="button"
            >
              Cargar más
            </button>
          ) : (
            ""
          )}
        </div>
      ) : null}
    </>
  );
}

function buildResetKey(params: {
  readonly availability?: AvailabilityFilter;
  readonly familySlug?: string;
  readonly initialProducts: readonly PublicCatalogProduct[];
  readonly pageSize: number;
  readonly productType?: "original" | "generico";
  readonly query?: string;
  readonly sort: CatalogSort;
  readonly total: number;
}): string {
  return [
    params.availability ?? "",
    params.familySlug ?? "",
    params.pageSize,
    params.productType ?? "",
    params.query ?? "",
    params.sort,
  ].join("|");
}

function buildSearchUrl(params: {
  readonly availability?: AvailabilityFilter;
  readonly familySlug?: string;
  readonly limit: number;
  readonly offset: number;
  readonly productType?: "original" | "generico";
  readonly query?: string;
  readonly sort: CatalogSort;
}): string {
  const searchParams = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
    sort: params.sort,
  });

  if (params.availability) {
    searchParams.set("availability", params.availability);
  }

  if (params.familySlug) {
    searchParams.set("familySlug", params.familySlug);
  }

  if (params.productType) {
    searchParams.set("productType", params.productType);
  }

  if (params.query) {
    searchParams.set("query", params.query);
  }

  return `/api/catalog/search?${searchParams.toString()}`;
}
